import React, { useState, useMemo, useEffect, useRef } from "react";
import * as PIXI from "pixi.js";
import "leaflet-pixi-overlay";
import { useMap } from "react-leaflet";
import L from "leaflet";
import { useTheme } from "@mui/material";
import { useOutletContext } from "react-router-dom";
import drawMarker from "./components/drawMaker";
import { drawConnection } from "./components/drawLine";
import PopupContent from "./components/popupContent";
import isEqual from "fast-deep-equal/react";

PIXI.settings.FAIL_IF_MAJOR_PERFORMANCE_CAVEAT = false;
PIXI.settings.ROUND_PIXELS = true;
PIXI.utils.skipHello();
PIXI.Ticker.shared.maxFPS = 30;

const { Assets } = PIXI;

function PixiOverlay({
  markers = [],
  elementSelected,
  editable,
  onClick = () => {},
  onClickLine = () => {},
  onBackgroundClick = () => {},
  handleUpdatePositionElement = () => {},
  iconsElements,
  disable = false,
  showLabel = false,
  onRadiusChange = () => {},
  rulerMode,
  connections = [],
  zoom,
  editMode = false,
  focusNodeId,
  configurationMap = {},
  connectionsDataMap = new Map(),
  configElementDataMap = new Map(),
  disableLinesZoom,
}) {
  const { config, configLayer } = useOutletContext();
  const spriteByIdRef = useRef(new Map());
  const explosionsRef = useRef(new Map());

  const theme = useTheme();
  const colorMode = theme.palette.mode;
  const layer = configLayer?.[markers[0]?.mapId];

  /* ---------- STATE ---------- */
  const [texturesLoaded, setTexturesLoaded] = useState(false);
  const [pixiOverlay, setPixiOverlay] = useState(null);
  const [labelTextures, setLabelTextures] = useState(new Map());
  const tickerFantasmaRef = useRef(null);
  /* Índice do Supercluster e mapeamento id->marker */
  const id2MarkerRef = useRef(new Map());

  /* Radio do node */
  const lastRadiusRef = useRef(null);

  /* ---------- MAP ---------- */
  const map = useMap();

  const textureKeys = useMemo(() => {
    const baseKeys = [
      ...new Set(markers.map((m) => m.iconId).filter(Boolean)),
    ].map((key) => key);

    let baseKey = [];
    if (elementSelected && elementSelected?.tempId) {
      baseKey = [elementSelected.img];
    }
    // filter(Boolean): caixas de texto (tipo 3) têm img null e não viram textura
    return [...baseKeys, ...baseKey].filter(Boolean);
  }, [markers, elementSelected]);

  /* ---------- EFFECT: carrega texturas novas ---------- */
  useEffect(() => {
    if (!textureKeys.length) return;
    setTexturesLoaded(false);

    const promises = textureKeys.map((texKey) => {
      if (Assets.cache.has(texKey)) return Promise.resolve();

      // segurança – se faltar PNG cria SVG genérico
      Assets.add(texKey, getDefaultIcon());

      // if (texKey === "cluster_icon") {
      //   Assets.add(texKey, getClusterIcon());
      // }
      return Assets.load(texKey);
    });

    Promise.all(promises)
      .then(() => setTexturesLoaded(true))
      .catch((err) => console.error("Falha ao carregar texturas:", err));
  }, [textureKeys]);

  /* ---------- EFFECT: cria overlay uma única vez ---------- */

  useEffect(() => {
    if (!map) return;

    const container = new PIXI.Container();

    container.eventMode = "static";
    container.interactive = true;
    container.interactiveChildren = true;
    container.sortableChildren = true;

    const overlay = L.pixiOverlay(
      (utils) => {
        const scale = utils.getScale();
        const renderer = utils.getRenderer();
        const container = utils.getContainer();
        renderer.reset();

        // Tamanho dinâmico baseado no zoom
        container.children.forEach((c) => {
          if (c.isBg) return;
          let nodeSize = c.isConnection
            ? 2.8 // ficou esse devido a calibração ter sido feita enquanto o marker estava nesse tamanho
            : config?.configuracoes?.geo?.nodeSize;
          const RADIUS_PX = c.isCluster
            ? 0.4 + (parseFloat(nodeSize) || 5) / 20
            : 0.3 + (parseFloat(nodeSize) || 5) / 20;
          const invScale = 1 / scale;

          c.scale.set(RADIUS_PX * invScale);
        });
        renderer.render(container);
      },
      container,
      {
        destroyInteractionManager: false,
      },
    );

    overlay.addTo(map);
    setPixiOverlay(overlay);

    return () => overlay.remove();
  }, [map, config]);

  useEffect(() => {
    const labelTextures = new Map();
    const isLight = colorMode === "light";
    const fillColor = isLight ? "#202020ff" : "#e6e6e6";
    const haloColor = isLight ? "#e6e6e6" : "#202020ff";
    for (const m of markers) {
      const txt = new PIXI.Text(m.label || m.titulo, {
        fontSize: 28,
        fontWeight: "bold",
        fill: fillColor,
        stroke: haloColor,
        strokeThickness: 5,
        lineJoin: "round",
        dropShadow: true,
        dropShadowColor: haloColor,
        dropShadowAlpha: 0.6,
        dropShadowBlur: 4,
        dropShadowDistance: 0,

        align: "center",
        padding: 6,
      });
      txt.resolution = 2;
      // garante que o texto foi renderizado internamente
      if (typeof txt.updateText === "function")
        try {
          txt.updateText();
        } catch (e) {}
      // usa txt.texture diretamente (já é PIXI.Texture) e stringifica a chave
      labelTextures.set(String(m.id), txt.texture);
    }
    setLabelTextures(labelTextures);
  }, [markers, colorMode]);

  /* ---------- EFFECT: desenha sprites ---------- */
  useEffect(() => {
    if (!pixiOverlay || !texturesLoaded) return;

    const { utils } = pixiOverlay;
    const container = utils.getContainer();
    const project = utils.latLngToLayerPoint;

    // sempre que reconstruir, zera o mapa de sprites
    spriteByIdRef.current.clear();

    //limpa apenas os markers and background
    container.children
      .filter((child) => !child.isConnection)
      .forEach((child) => container.removeChild(child));

    drawBackground(container);

    if (!disable) {
      markers
        .filter((marker) => marker?.id !== elementSelected?.id || !editable)
        .forEach((marker) =>
          drawMarker({
            m: marker,
            container,
            project,
            pixiOverlay,
            colorMode,
            editable,
            showLabel,
            elementSelected,
            handleUpdatePositionElement,
            onClick,
            map,
            rulerMode,
            spriteByIdRef,
            explosionsRef,
            configElementDataMap,
            theme,
            labelTexture: labelTextures.get(marker.id),
            editMode,
          }),
        );

      if (
        editable &&
        elementSelected &&
        elementSelected.tipo !== 3 &&
        elementSelected.tipo !== 4
      ) {
        const iconExists = iconsElements?.some(
          (icon) => icon.id === elementSelected.img,
        );
        const formatelementSelected = {
          ...elementSelected,
          id: elementSelected?.id ?? elementSelected?.tempId,
          position: [
            parseFloat(
              elementSelected?.elementConfig?.config?.coordenadas?.lat,
            ),
            parseFloat(
              elementSelected?.elementConfig?.config?.coordenadas?.long,
            ),
          ],
          iconId: iconExists
            ? elementSelected.img
            : iconsElements?.find(
                (icon) =>
                  icon.nome ==
                  (elementSelected.tipo === 0
                    ? "_roteador"
                    : elementSelected.tipo === 1
                      ? "_mapa"
                      : "_pop"),
              )?.id, // caso não tenha, recebe o id com base no tipo de elemento
          baseIcon: "marker_circle",
          tint: theme.palette.color?.zabbixSeverty?.selected,
          severity: 1,
          label: elementSelected.titulo ?? elementSelected.id,
        };

        drawMarker({
          m: formatelementSelected,
          container,
          project,
          pixiOverlay,
          colorMode,
          editable,
          showLabel,
          elementSelected,
          handleUpdatePositionElement,
          onClick,
          map,
          rulerMode,
          spriteByIdRef,
          explosionsRef,
          configElementDataMap,
          theme,
          labelTexture: labelTextures.get(formatelementSelected.id),
          editMode,
        });
      }
    }
    return () => {
      container.children
        .filter((child) => !child.isConnection)
        .forEach((child) => {
          if (child.destroyFn) child.destroyFn();
          container.removeChild(child);
        });
    };
  }, [
    pixiOverlay,
    texturesLoaded,
    colorMode,
    markers,
    map,
    elementSelected,
    editable,
    iconsElements,
    disable,
    showLabel,
    rulerMode,
    editMode,
    configElementDataMap,
    labelTextures,
    configurationMap?.configuracoes?.animacao?.alertas,
  ]);

  useEffect(() => {
    if (!pixiOverlay || disableLinesZoom) return;

    const { utils } = pixiOverlay;
    const container = utils.getContainer();
    const project = utils.latLngToLayerPoint;

    container.children
      .filter((child) => child.isConnection)
      .forEach((child) => container.removeChild(child));

    if (!disable) {
      connections.forEach((connection) =>
        drawConnection(
          container,
          project,
          connection,
          connectionsDataMap.get(connection.id),
          map,
          zoom,
          PopupContent,
          theme,
          rulerMode,
          onClickLine,
          configurationMap?.configuracoes?.animacao?.linha,
          editMode,
          layer,
          colorMode,
          configurationMap?.configuracoes?.larguraEnlace,
        ),
      );
    }
    return () => {
      container.children
        .filter((child) => child.isConnection)
        .forEach((child) => {
          if (child.destroyFn) child.destroyFn();
          container.removeChild(child);
        });

      map.eachLayer((layer) => {
        if (layer instanceof L.Popup || layer instanceof L.Tooltip) {
          map.removeLayer(layer);
        }
      });
    };
  }, [
    pixiOverlay,
    connections,
    map,
    zoom,
    disable,
    rulerMode,
    editMode,
    configurationMap?.configuracoes?.animacao?.linha,
    connectionsDataMap,
    disableLinesZoom,
    colorMode,
    layer,
    configurationMap?.configuracoes?.larguraEnlace,
  ]);

  const drawBackground = (container) => {
    const bg = new PIXI.Graphics();
    bg.isBg = true;
    const size = 1e10;
    const hitArea = new PIXI.Rectangle(-size, -size, size * 2, size * 2);
    bg.hitArea = hitArea;
    bg.interactive = true;
    bg.eventMode = "static";

    let pointerDownTime = 0;
    const CLICK_THRESHOLD = 250; // ms

    bg.on("mousedown", () => {
      pointerDownTime = Date.now();
    });

    bg.on("pointertap", (e) => {
      const duration = Date.now() - pointerDownTime;
      if (duration < CLICK_THRESHOLD) {
        e.stopPropagation();
        if (typeof onBackgroundClick === "function") {
          onBackgroundClick();
        }
      }
    });

    container.addChild(bg);
  };

  useEffect(() => {
    if (!pixiOverlay) return;

    const valueRef = spriteByIdRef.current.values().next().value;
    if (!valueRef) return;

    const baseRadiusPx =
      valueRef.baseRadiusPx ??
      Math.max(
        (valueRef.circle?.width ?? 0) / 2,
        (valueRef.sprite?.width ?? 0) / 2,
      );
    const nodeScale =
      0.3 + (parseFloat(config?.configuracoes?.geo?.nodeSize) || 5) / 20;
    const radiusPx = baseRadiusPx * nodeScale;

    if (Number.isFinite(radiusPx) && radiusPx !== lastRadiusRef.current) {
      lastRadiusRef.current = radiusPx;
      onRadiusChange?.(radiusPx);
    }
  }, [pixiOverlay, config?.configuracoes?.geo?.nodeSize, onRadiusChange]);

  useEffect(() => {
    const ticker = PIXI.Ticker.shared;
    const spritesRef = spriteByIdRef.current;
    const configMap = configElementDataMap;
    const explosions = explosionsRef.current;

    // Parâmetros de controle
    const blinkSpeed = 0.008;
    const minAlpha = 0.2;

    const animatedIds = new Set(
      [elementSelected?.id ?? elementSelected?.tempId, focusNodeId].filter(
        Boolean,
      ),
    );
    const globalTicker = () => {
      const t = ticker.lastTime;
      // === 1️⃣ Escala do elemento selecionado ===
      if (animatedIds.size > 0) {
        const scale = 1 + 0.16 * Math.sin(t / 150);
        for (const id of animatedIds) {
          const s = spritesRef.get(id);
          if (s) {
            s.sprite.scale.set(
              s.sprite.baseScale.x * scale,
              s.sprite.baseScale.y * scale,
            );
            s.circle.scale.set(scale);
            s.circleBase.scale.set(scale);
          }
        }
      }

      if (editable) {
        return;
      }

      if (configurationMap?.configuracoes?.animacao?.alertas != false) {
        // === 2️⃣ Explosões (alertas) ===
        const delta = ticker.deltaMS / 1000;
        for (const [, data] of explosions) {
          data.t += delta;
          const scaleFactor = 1 + data.t * 2;
          data.graphic.scale.set(scaleFactor);
          data.graphic.alpha = Math.max(0, 0.7 - data.t * 0.7);
          if (data.t > 1) {
            data.t = 0;
            data.graphic.scale.set(1);
            data.graphic.alpha = 0.6;
          }
        }
      }

      // === 3️⃣ Piscar bordas ===
      const alpha =
        minAlpha + (1 - minAlpha) * (0.5 + 0.5 * Math.sin(t * blinkSpeed));
      for (const [key, { circleBase }] of spritesRef) {
        if (configMap.get(key)?.isDown && !editable) {
          circleBase.alpha = alpha;
        }
      }
    };
    ticker.add(globalTicker);
    return () => ticker.remove(globalTicker);
  }, [
    configurationMap?.configuracoes?.animacao?.alertas,
    configElementDataMap,
    elementSelected?.id,
    elementSelected?.tempId,
    focusNodeId,
    editable,
  ]);

  // Esse ticker garante a atualização global por frame do pixiOverlay
  useEffect(() => {
    if (!pixiOverlay) return;
    const ticker = PIXI.Ticker.shared;

    // se já adicionamos antes, remove para evitar duplicados
    if (tickerFantasmaRef.current) {
      try {
        ticker.remove(tickerFantasmaRef.current);
      } catch (e) {}
    }

    const tickerFantasma = () => {
      pixiOverlay.redraw?.();
    };

    // guarda a referência para limpeza futura
    tickerFantasmaRef.current = tickerFantasma;
    ticker.add(tickerFantasma);

    return () => {
      if (tickerFantasmaRef.current) {
        ticker.remove(tickerFantasmaRef.current);
        tickerFantasmaRef.current = null;
      }
    };
  }, [pixiOverlay]);

  return null;
}

/* ---------- fallback: icone SVG azul ---------- */
function getDefaultIcon() {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
<path d="M12.8 32V20.7059H19.2V32H27.2V16.9412H32L16 0L0 16.9412H4.8V32H12.8Z" fill="white"/>
</svg>
`;
  const b64 = btoa(unescape(encodeURIComponent(svg)));
  return `data:image/svg+xml;base64,${b64}`;
}

/* ---------- cluster icon ---------- */
// function getClusterIcon() {
//   const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
//     <circle cx="20" cy="20" r="20" fill="white"/>
//   </svg>`;
//   const b64 = btoa(unescape(encodeURIComponent(svg)));
//   return `data:image/svg+xml;base64,${b64}`;
// }

function mapsEqual(a, b) {
  if (a === b) return true;
  if (a == null || b == null) return a === b;
  if (!(a instanceof Map) || !(b instanceof Map)) return false;
  if (a.size !== b.size) return false;

  for (const [key, valA] of a) {
    if (!b.has(key)) return false;
    const valB = b.get(key);
    if (!isEqual(valA, valB)) return false;
  }
  return true;
}

function propsAreEqual(prev, next) {
  // Comparações diretas (valores simples ou estáveis)
  if (prev.editable !== next.editable) return false;
  if (prev.disable !== next.disable) return false;
  if (prev.showLabel !== next.showLabel) return false;
  if (prev.zoom !== next.zoom) return false;
  if (prev.editMode !== next.editMode) return false;
  if (prev.focusNodeId !== next.focusNodeId) return false;
  if (prev.rulerMode !== next.rulerMode) return false;
  if (prev.disableLinesZoom !== next.disableLinesZoom) return false;

  // Comparações profundas (objetos e arrays)
  if (!isEqual(prev.markers, next.markers)) return false;
  if (!isEqual(prev.connections, next.connections)) return false;
  if (!isEqual(prev.elementSelected, next.elementSelected)) return false;
  if (!isEqual(prev.iconsElements, next.iconsElements)) return false;
  if (!isEqual(prev.configurationMap, next.configurationMap)) return false;
  // <-- aqui usamos mapsEqual para Map objects -->
  if (!mapsEqual(prev.connectionsDataMap, next.connectionsDataMap))
    return false;
  if (!mapsEqual(prev.configElementDataMap, next.configElementDataMap))
    return false;

  return true;
}
export default React.memo(PixiOverlay, propsAreEqual);
